<script setup lang="ts">

import TrialStatusContainer from "@/trial/components/trial-status/TrialStatusContainer.vue";
import Icon from "@/core/components/icon/Icon.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {useRouter} from "vue-router";
import HomeSponsoredExtensions from "@/home/components/HomeSponsoredExtensions.vue";
import HomeSponsoredExtensionCard from "@/home/components/HomeSponsoredExtensionCard.vue";
import useHealthcenterAdvantages from "@/advantage/composables/useHealthcenterAdvantages";
import MarkdownView from "@/core/components/markdown/Markdown.vue";
import {Calculator} from "@lucide/vue";
import {Skeleton} from "@/core/components/ui/skeleton";
import AdvantagesAnimatedButton from "@/advantage/components/AdvantagesAnimatedButton.vue";
import GroupingAdvantageButton from "@/advantage/components/GroupingAdvantageButton.vue";

const router = useRouter();

const {data, isLoading} = useHealthcenterAdvantages();

const navigateToGlobalPlan = () => {
    router.push({name: 'subscription-plans'});
};
</script>

<template>
  <TrialStatusContainer>
    <template #headline>
      <div class="space-y-2">
        <div class="flex items-center justify-between flex-wrap gap-2">
          <div class="flex-1">
            <p class="text-lg">
              Vous bénéficiez d'Apodis <strong>Freemium (Gratuit)</strong>
            </p>
            <p class="text-primary">
              Vous bénéficiez des modules gratuits ainsi que des fonctionnalités de base Apodis.
            </p>
          </div>
          <div class="space-y-1">
            <BaseButton class="text-primary" :to="{name: 'extensions-profitability'}">
              <Calculator class="h-6 square mr-1 text-black" />
              Combien me fait gagner Apodis ?
            </BaseButton>
          </div>
        </div>
      </div>
      <HomeSponsoredExtensions class="bg-transparent pl-0 pr-0">
        <template #title>
          <div />
        </template>
        <router-link :to="{name: 'subscription-plans', query: {scrollTo: 'feature-grid'}}">
          <HomeSponsoredExtensionCard name="Les basiques Apodis" :prefix-name="false">
            <template #logo>
              <div class="w-16 h-16 flex items-center justify-center">
                <img src="@/assets/images/gift-ribbon.png" alt="Basiques Apodis">
              </div>
            </template>
          </HomeSponsoredExtensionCard>
        </router-link>
      </HomeSponsoredExtensions>
      <hr class="my-4 border-primary">
      <div class="space-y-1">
        <div>
          <div class="float-right">
            <BaseButton primary @click="navigateToGlobalPlan">
              <Icon name="mdi-crown" class="mr-1" color="secondary" :size="30" />
              Passer premium
            </BaseButton>
            <div v-if="data?.length" class="text-center text-sm text-primary mt-0.5">
              <p>🎁 {{ data[0].title }}</p>
            </div>
          </div>
          <h1 class="text-black">
            Passez Premium pour activer tous les autres modules
          </h1>
        </div>
        <div>
          <Skeleton v-if="isLoading" class="w-48 h-4" />
          <div v-else class="flex flex-col items-start">
            <div v-for="advantage in data" :key="advantage.id" class="flex items-baseline">
              <p>🎁</p>
              <MarkdownView
                :content="advantage.sales_pitch" class="text-primary font-bold text-lg"
                :inherit-styles="true"
              />
            </div>
            <div class="flex flex-wrap gap-3">
              <GroupingAdvantageButton :healthcenter-has-advantages="!!data?.length" />
              <AdvantagesAnimatedButton />
            </div>
          </div>
        </div>
      </div>
    </template>
  </TrialStatusContainer>
</template>

<style scoped>
.sales-pitch {
    @apply text-primary;
}

:deep(.sales-pitch strong) {
    @apply text-primary font-content-bold;
}
</style>
